import { Link, createFileRoute } from '@tanstack/react-router';
import {
  ChevronRight,
  KeyRound,
  Monitor,
  Moon,
  Shield,
  Sparkles,
  SquareTerminal,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthenticatedSession } from '@/lib/session';
import { useTheme, type ThemePreference } from '@/lib/theme';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/account/settings')({ component: SettingsHub });

const THEMES: { value: ThemePreference; label: string; icon: LucideIcon }[] = [
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'system', label: 'System', icon: Monitor },
];

/** Preferences that aren't part of the profile: appearance, Hearth AI and access shortcuts. */
function SettingsHub() {
  const { user } = useAuthenticatedSession();
  return (
    <div className="grid gap-6">
      <AppearanceCard />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="bg-brand/12 text-brand ring-brand/30 grid size-6 place-items-center rounded-md ring-1 ring-inset">
              <Sparkles className="size-3.5" aria-hidden />
            </span>
            Hearth AI
          </CardTitle>
          <CardDescription>
            Choose whether Hearth AI uses the panel’s provider or your own API key, pick a model and
            see what you have used.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <HubLink
            to="/account/ai"
            icon={Sparkles}
            title="AI settings"
            detail="Provider, model, your own keys and usage"
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Sign-in and access</CardTitle>
          <CardDescription>
            How you and your tools get into {user.isAdmin ? 'the panel' : 'your servers'}.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2">
          <HubLink
            to="/account/security"
            icon={Shield}
            title="Security"
            detail={
              user.twoFactorEnabled
                ? 'Password, two-factor (on) and sessions'
                : 'Password, two-factor (off) and sessions'
            }
          />
          <HubLink
            to="/account/ssh-keys"
            icon={KeyRound}
            title="SSH keys"
            detail="Keys for SFTP sign-in"
          />
          <HubLink
            to="/account/api-keys"
            icon={SquareTerminal}
            title="API keys"
            detail="Keys for the client API"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function AppearanceCard() {
  const { preference, setPreference } = useTheme();
  return (
    <Card>
      <CardHeader>
        <CardTitle id="appearance-title">Appearance</CardTitle>
        <CardDescription>Remembered in this browser only.</CardDescription>
      </CardHeader>
      <CardContent>
        <div
          role="radiogroup"
          aria-labelledby="appearance-title"
          className="grid grid-cols-3 gap-2 sm:max-w-md"
        >
          {THEMES.map(({ value, label, icon: Icon }) => {
            const checked = preference === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={checked}
                onClick={() => setPreference(value)}
                className={cn(
                  'flex h-16 flex-col items-center justify-center gap-1.5 rounded-lg border text-sm font-medium transition-colors',
                  'focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]',
                  checked
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                <Icon className={cn('size-4', checked && 'text-brand')} aria-hidden />
                {label}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function HubLink({
  to,
  icon: Icon,
  title,
  detail,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  detail: string;
}) {
  return (
    <Link
      // /account/ai is the AI workstream's page; the others are typed routes.
      to={to as never}
      className="group hover:bg-accent focus-visible:ring-ring/50 -mx-2 flex items-center gap-3 rounded-md px-2 py-2 outline-none focus-visible:ring-[3px]"
    >
      <span className="bg-elevated text-muted-foreground grid size-8 shrink-0 place-items-center rounded-md">
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="grid min-w-0 flex-1 leading-tight">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-muted-foreground truncate text-xs">{detail}</span>
      </span>
      <ChevronRight
        className="text-subtle group-hover:text-foreground size-4 shrink-0"
        aria-hidden
      />
    </Link>
  );
}
